import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  CancelOutlined as NotEligibleIcon,
} from '@mui/icons-material';
import { csAdminApi } from '../../services/api';
import { mapDirectorFromApi } from '../../utils/apiMappers';

const OFFICER_POSITIONS = [
  { value: 'ceo', label: 'Chief Executive Officer (CEO)' },
  { value: 'cfo', label: 'Chief Financial Officer (CFO)' },
  { value: 'coo', label: 'Chief Operating Officer (COO)' },
  { value: 'cto', label: 'Chief Technology Officer (CTO)' },
  { value: 'company_secretary', label: 'Company Secretary' },
  { value: 'general_counsel', label: 'General Counsel / Legal Officer' },
  { value: 'compliance_officer', label: 'Compliance Officer' },
  { value: 'treasurer', label: 'Treasurer' },
  { value: 'controller', label: 'Controller' },
  { value: 'other', label: 'Other' },
];

const AUTHORITY_LEVELS = [
  { value: 'full', label: 'Full Authority' },
  { value: 'limited', label: 'Limited Authority' },
  { value: 'joint', label: 'Joint Signatory' },
  { value: 'none', label: 'No Signing Authority' },
];

// Reports To options for governance hierarchy
const REPORTS_TO_TYPES = [
  { value: 'board', label: 'Board of Directors' },
  { value: 'chairman', label: 'Chairman of the Board' },
  { value: 'ceo', label: 'Chief Executive Officer (CEO)' },
  { value: 'committee', label: 'Committee' },
  { value: 'officer', label: 'Other Officer' },
];

const AddOfficerModal = ({
  open, 
  onClose, 
  onAdd, 
  companyId, 
  companyName, 
  existingOfficers = [],
  editOfficer = null 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [availablePeople, setAvailablePeople] = useState([]);
  const [filteredPeople, setFilteredPeople] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [addNewPerson, setAddNewPerson] = useState(false);
  const [committees, setCommittees] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    customPosition: '',
    appointmentDate: '',
    authorityLevel: 'limited',
    isAuthorizedSignatory: false,
    reportsToType: '',
    reportsToCommittee: '',
    reportsToOfficer: '',
    notes: '',
  });

  const isEditing = !!editOfficer;

  // Helper to map position label back to value
  const getPositionValue = (positionLabel) => {
    if (!positionLabel) return '';
    const found = OFFICER_POSITIONS.find(p => 
      p.label.toLowerCase() === positionLabel.toLowerCase() ||
      p.value === positionLabel
    );
    return found ? found.value : 'other';
  };

  // Helper to map authority level label back to value
  const getAuthorityValue = (authorityLabel) => {
    if (!authorityLabel) return 'limited';
    const found = AUTHORITY_LEVELS.find(a => 
      a.label.toLowerCase() === authorityLabel.toLowerCase() ||
      a.value === authorityLabel
    );
    return found ? found.value : authorityLabel;
  };

  // Populate form data when editing
  useEffect(() => {
    if (open && editOfficer) {
      const positionValue = getPositionValue(editOfficer.position);
      const isCustomPosition = positionValue === 'other' && editOfficer.position;
      
      setFormData({
        name: editOfficer.name || '',
        email: editOfficer.email || '',
        phone: editOfficer.phone || '',
        position: positionValue,
        customPosition: isCustomPosition ? editOfficer.position : '',
        appointmentDate: editOfficer.appointmentDate 
          ? new Date(editOfficer.appointmentDate).toISOString().split('T')[0] 
          : '',
        authorityLevel: getAuthorityValue(editOfficer.authorityLevel),
        isAuthorizedSignatory: editOfficer.isAuthorizedSignatory ?? false,
        reportsToType: editOfficer.reportsToType || '',
        reportsToCommittee: editOfficer.reportsToCommittee || '',
        reportsToOfficer: editOfficer.reportsToOfficer || '',
        notes: editOfficer.notes || '',
      });
      setAddNewPerson(true); // Skip person selection when editing
    } else if (open && !editOfficer) {
      // Reset form for new officer
      setFormData({
        name: '',
        email: '',
        phone: '',
        position: '',
        customPosition: '',
        appointmentDate: '',
        authorityLevel: 'limited',
        isAuthorizedSignatory: false,
        reportsToType: '',
        reportsToCommittee: '',
        reportsToOfficer: '',
        notes: '',
      });
      setAddNewPerson(false);
    }
  }, [open, editOfficer]);

  // Fetch available people (directors/existing personnel) on open
  useEffect(() => {
    if (open && !isEditing) {
      fetchAvailablePeople();
    }
    if (open && companyId) {
      csAdminApi.getCommittees({ company_id: companyId })
        .then((res) => {
          const list = (res.data?.data || []).map((c) => ({
            id: c.id || c.committee_id,
            name: c.name || c.committee_name,
          }));
          setCommittees(list);
        })
        .catch(() => setCommittees([]));
    }
  }, [open, companyId, isEditing]);

  // Filter people based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredPeople(availablePeople);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = availablePeople.filter(person =>
        person.name.toLowerCase().includes(query) ||
        person.email?.toLowerCase().includes(query) ||
        person.currentRole?.toLowerCase().includes(query)
      );
      setFilteredPeople(filtered);
    }
  }, [searchQuery, availablePeople]);

  const fetchAvailablePeople = async () => {
    setLoading(true);
    setError(null);

    try {
      if (companyId) {
        const res = await csAdminApi.getCompanyDirectors(companyId);
        const existingNames = new Set(
          existingOfficers.map((o) => (o.name || o.officer_name || '').toLowerCase())
        );
        const people = (res.data?.data || [])
          .map((row) => {
            const m = mapDirectorFromApi(row);
            const name = m.full_name || `${m.firstName} ${m.lastName}`.trim();
            const alreadyOfficer = existingNames.has(name.toLowerCase());
            return {
              id: m.id,
              name,
              email: m.email || '',
              phone: '',
              currentRole: m.position || 'Director',
              isEligible: !alreadyOfficer,
              ineligibilityReason: alreadyOfficer ? 'Already an officer in this company' : null,
            };
          })
          .filter((p) => p.name);
        setAvailablePeople(people);
        setFilteredPeople(people);
        return;
      }

      setAvailablePeople([]);
      setFilteredPeople([]);
    } catch (err) {
      setError('Failed to load personnel. Please try again.');
      console.error('Fetch error:', err);
      setAvailablePeople([]);
      setFilteredPeople([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPerson = (person) => {
    if (!person.isEligible) {
      setError(person.ineligibilityReason || 'This person is not eligible');
      return;
    }
    setSelectedPerson(person);
    setFormData({
      ...formData,
      name: person.name,
      email: person.email || '',
      phone: person.phone || '',
    });
    setError(null);
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [field]: value });
    setError(null);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Officer name is required');
      return false;
    }
    if (!formData.position) {
      setError('Please select a position');
      return false;
    }
    if (formData.position === 'other' && !formData.customPosition.trim()) {
      setError('Please specify the position');
      return false;
    }
    if (!formData.appointmentDate) {
      setError('Appointment date is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    setError(null);

    try {
      const positionLabel = formData.position === 'other'
        ? formData.customPosition
        : OFFICER_POSITIONS.find((p) => p.value === formData.position)?.label;

      let reportsToLabel = '';
      if (formData.reportsToType === 'committee' && formData.reportsToCommittee) {
        const committee = committees.find((c) => String(c.id) === String(formData.reportsToCommittee));
        reportsToLabel = committee?.name || '';
      } else if (formData.reportsToType === 'officer' && formData.reportsToOfficer) {
        const officer = existingOfficers.find((o) => String(o.id) === String(formData.reportsToOfficer));
        reportsToLabel = officer?.name || '';
      } else if (formData.reportsToType) {
        reportsToLabel = REPORTS_TO_TYPES.find((r) => r.value === formData.reportsToType)?.label || '';
      }

      const officerData = {
        id: editOfficer?.id || Date.now(),
        personId: selectedPerson?.id || null,
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        position: positionLabel,
        positionCode: formData.position,
        appointmentDate: formData.appointmentDate,
        authorityLevel: formData.authorityLevel,
        isAuthorizedSignatory: formData.isAuthorizedSignatory,
        reportsToType: formData.reportsToType,
        reportsToCommittee: formData.reportsToCommittee,
        reportsToOfficer: formData.reportsToOfficer,
        reportsToLabel: reportsToLabel,
        notes: formData.notes.trim(),
        status: 'Active',
      };

      if (onAdd) {
        await Promise.resolve(onAdd(officerData));
      }

      handleClose();
    } catch (err) {
      setError('Failed to save officer. Please try again.');
      console.error('Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedPerson(null);
    setAddNewPerson(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      position: '',
      customPosition: '',
      appointmentDate: '',
      authorityLevel: 'limited',
      isAuthorizedSignatory: false,
      reportsToType: '',
      reportsToCommittee: '',
      reportsToOfficer: '',
      notes: '',
    });
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            {isEditing ? 'Edit Officer' : 'Add Officer'}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ color: 'grey.500' }}
            disabled={submitting}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Company Info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Adding officer to:
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {companyName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Current officers: {existingOfficers?.length || 0}
          </Typography>
        </Box>

        {/* Select from existing or add new */}
        {!isEditing && (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={addNewPerson}
                  onChange={(e) => {
                    setAddNewPerson(e.target.checked);
                    if (e.target.checked) {
                      setSelectedPerson(null);
                      setFormData({ ...formData, name: '', email: '', phone: '' });
                    }
                  }}
                  disabled={submitting}
                />
              }
              label="Add new person (not in system)"
              sx={{ mb: 2 }}
            />

            {!addNewPerson && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Select Person <span style={{ color: '#d32f2f' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Search by name, email, or role..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={submitting || loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                {loading ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Loading personnel...
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Available ({filteredPeople.length}):
                    </Typography>
                    <List
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        maxHeight: 200,
                        overflow: 'auto',
                        bgcolor: 'background.paper',
                      }}
                    >
                      {filteredPeople.length === 0 ? (
                        <ListItem>
                          <ListItemText
                            primary="No personnel found"
                            secondary="Try a different search or add a new person"
                            sx={{ textAlign: 'center', py: 2 }}
                          />
                        </ListItem>
                      ) : (
                        filteredPeople.map((person) => (
                          <ListItem
                            key={person.id}
                            button
                            selected={selectedPerson?.id === person.id}
                            onClick={() => handleSelectPerson(person)}
                            disabled={!person.isEligible || submitting}
                            sx={{
                              borderBottom: 1,
                              borderColor: 'divider',
                              '&:last-child': { borderBottom: 0 },
                              opacity: person.isEligible ? 1 : 0.6,
                            }}
                          >
                            <ListItemIcon>
                              <Checkbox
                                edge="start"
                                checked={selectedPerson?.id === person.id}
                                disabled={!person.isEligible}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="body1" fontWeight={500}>
                                    {person.name}
                                  </Typography>
                                  {!person.isEligible && (
                                    <Chip
                                      label="Not Eligible"
                                      size="small"
                                      color="error"
                                      icon={<NotEligibleIcon />}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {person.currentRole} • {person.email}
                                  </Typography>
                                  {!person.isEligible && person.ineligibilityReason && (
                                    <Typography variant="caption" color="error.main">
                                      {person.ineligibilityReason}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        ))
                      )}
                    </List>
                  </>
                )}
              </Box>
            )}

            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* Officer Details Form */}
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Officer Details
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Name (editable if adding new or editing) */}
          <TextField
            fullWidth
            required
            label="Full Name"
            value={formData.name}
            onChange={handleChange('name')}
            disabled={submitting || (!addNewPerson && !isEditing && selectedPerson)}
          />

          {/* Contact in a row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              required
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              disabled={submitting}
            />
            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={handleChange('phone')}
              disabled={submitting}
            />
          </Box>

          {/* Position */}
          <FormControl fullWidth required>
            <InputLabel>Position</InputLabel>
            <Select
              value={formData.position}
              label="Position"
              onChange={handleChange('position')}
              disabled={submitting}
            >
              {OFFICER_POSITIONS.map((pos) => (
                <MenuItem key={pos.value} value={pos.value}>
                  {pos.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Custom Position if "Other" selected */}
          {formData.position === 'other' && (
            <TextField
              fullWidth
              required
              label="Specify Position"
              value={formData.customPosition}
              onChange={handleChange('customPosition')}
              disabled={submitting}
              placeholder="Enter the position title"
            />
          )}

          {/* Appointment Date and Authority Level */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              required
              label="Appointment Date"
              type="date"
              value={formData.appointmentDate}
              onChange={handleChange('appointmentDate')}
              InputLabelProps={{ shrink: true }}
              disabled={submitting}
            />
            <FormControl fullWidth>
              <InputLabel>Authority Level</InputLabel>
              <Select
                value={formData.authorityLevel}
                label="Authority Level"
                onChange={handleChange('authorityLevel')}
                disabled={submitting}
              >
                {AUTHORITY_LEVELS.map((level) => (
                  <MenuItem key={level.value} value={level.value}>
                    {level.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Authorized Signatory */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.isAuthorizedSignatory}
                onChange={(e) => setFormData({ ...formData, isAuthorizedSignatory: e.target.checked })}
                disabled={submitting}
              />
            }
            label="Authorized Signatory"
          />

          <Divider sx={{ my: 1 }} />

          {/* Reports To Section - for Governance Chart */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Reporting Structure (for Governance Chart)
          </Typography>

          <FormControl fullWidth>
            <InputLabel>Reports To</InputLabel>
            <Select
              value={formData.reportsToType}
              label="Reports To"
              onChange={(e) => {
                setFormData({ 
                  ...formData, 
                  reportsToType: e.target.value,
                  reportsToCommittee: '',
                  reportsToOfficer: '',
                });
              }}
              disabled={submitting}
            >
              <MenuItem value="">
                <em>Not specified</em>
              </MenuItem>
              {REPORTS_TO_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Committee Selection - when Reports To = Committee */}
          {formData.reportsToType === 'committee' && (
            <FormControl fullWidth>
              <InputLabel>Select Committee</InputLabel>
              <Select
                value={formData.reportsToCommittee}
                label="Select Committee"
                onChange={handleChange('reportsToCommittee')}
                disabled={submitting}
              >
                {committees.map((committee) => (
                  <MenuItem key={committee.id} value={committee.id}>
                    {committee.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Officer Selection - when Reports To = Other Officer */}
          {formData.reportsToType === 'officer' && (
            <FormControl fullWidth>
              <InputLabel>Select Officer</InputLabel>
              <Select
                value={formData.reportsToOfficer}
                label="Select Officer"
                onChange={handleChange('reportsToOfficer')}
                disabled={submitting}
              >
                {existingOfficers
                  .filter(o => o.id !== editOfficer?.id) // Exclude self when editing
                  .map((officer) => (
                    <MenuItem key={officer.id} value={officer.id}>
                      {officer.name} - {officer.position}
                    </MenuItem>
                  ))}
                {existingOfficers.filter(o => o.id !== editOfficer?.id).length === 0 && (
                  <MenuItem disabled>No other officers available</MenuItem>
                )}
              </Select>
            </FormControl>
          )}

          {/* Notes */}
          <TextField
            fullWidth
            label="Notes (optional)"
            value={formData.notes}
            onChange={handleChange('notes')}
            multiline
            rows={2}
            placeholder="Additional information about this officer"
            disabled={submitting}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !formData.name || !formData.position || !formData.appointmentDate || !formData.email}
        >
          {submitting ? 'Saving...' : isEditing ? 'Update Officer' : 'Add Officer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddOfficerModal;
